import { Dispatch } from '@reduxjs/toolkit';
import { CmcWebApi, IWebApiRequestBody } from '@cmctechnology/webinvest-api-client-web-trading';
import { ACCOUNT_STORE_NAME, actions, IStore } from './store';
import { frontendActions, getConfig, LocationType } from '@cmctechnology/webinvest-store-frontend';
import { ACCOUNT_API_NAMES } from './apis';
import { AccountKey } from './types';
import { ResourceKey } from '@cmctechnology/webinvest-store-frontend/dist/js/models/subscriptions';
import { marketdataActions } from '@cmctechnology/webinvest-store-marketdata';
import { instrumentActions } from '@cmctechnology/webinvest-store-instruments';

const PROFIT_LOSS_ITEMS_MAX_PAGE_SIZE = 1_000;

const fetchProfitLossPage = async (accountNo: AccountKey, page: number, isFirst: boolean, hash: number | undefined, dispatch: Dispatch<any>) => {
  const request: CmcWebApi.Account.ProfitLossRequest1 & IWebApiRequestBody = {
    AccountNumber: accountNo,
    Page: page,
    PageSize: PROFIT_LOSS_ITEMS_MAX_PAGE_SIZE,
    _config: { hash }
  };
  const response = await frontendActions.webTradingApiCall<CmcWebApi.Account.ProfitLossFullModel3>(
    ACCOUNT_STORE_NAME,
    ACCOUNT_API_NAMES.ProfitLossGetFull3,
    request,
    dispatch,
    accountNo,
    isFirst,
    true
  );
  return response;
};

export const fetchProfitLoss = (accountNo: AccountKey, first?: boolean) => async (dispatch: Dispatch<any>, getState: () => IStore) => {
  try {
    const hash = getState()[ACCOUNT_STORE_NAME].profitLoss[accountNo]?.hash ?? 0;
    const isFirst = first ?? !hash;
    const response = await fetchProfitLossPage(accountNo, 0, isFirst, hash, dispatch);

    // If more than one page fetch the rest to get all of them
    if (response.TotalPages > 1) {
      const otherPages = await Promise.all(
        Array.from({ length: response.TotalPages - 1 }).map((_, i) => fetchProfitLossPage(accountNo, i + 1, isFirst, undefined, dispatch))
      );
      response.ClientHoldings = [...response.ClientHoldings, ...otherPages.flatMap((otherPage) => otherPage.ClientHoldings)];
    }

    dispatch<any>(actions.profitLossFetched({ accountNo, hash: response._apiInfo?.Hash ?? 0, profitLoss: response }));

    if (isFirst) {
      const quotes = response.ClientHoldings.map((holding) => holding.Quote).filter(Boolean);
      dispatch<any>(marketdataActions.quoteFetched(quotes));
      dispatch<any>(
        instrumentActions.exchangeFetched(
          response.ClientHoldings.map((holding) => ({ instrument: holding.InstrumentDisplayCode, exchangeId: holding.Quote.ExchangeId }))
        )
      );
    }
  } catch {}
};

// eslint-disable-next-line unicorn/consistent-function-scoping
export const onPollProfitLoss = (subscriptions: ResourceKey[] = []) => {
  if (subscriptions.length === 0) return () => {};
  return (dispatch: Dispatch<any>) => {
    subscriptions.forEach((key) => {
      dispatch<any>(fetchProfitLoss(key as AccountKey));
    });
  };
};

export const subscribeProfitLoss = (location: LocationType, accountNo: AccountKey) => async (dispatch: Dispatch<any>, getState: () => IStore) => {
  if (getState()[ACCOUNT_STORE_NAME].profitLoss[accountNo]) {
    dispatch<any>(actions.profitLossHashReset(accountNo));
  } else {
    await fetchProfitLoss(accountNo)(dispatch, getState);
  }

  dispatch<any>(
    frontendActions.subscribe({
      onPoll: onPollProfitLoss,
      location,
      apiName: ACCOUNT_API_NAMES.ProfitLossGetFull3,
      storeName: ACCOUNT_STORE_NAME,
      resourceKeys: [accountNo],
      pollTimeout: getConfig().pollTimeout
    })
  );
};

export const unsubscribeProfitLoss = (location: LocationType, accountNo: AccountKey) => (dispatch: Dispatch<any>) => {
  dispatch<any>(
    frontendActions.unsubscribe({
      location,
      apiName: ACCOUNT_API_NAMES.ProfitLossGetFull3,
      storeName: ACCOUNT_STORE_NAME,
      resourceKeys: [accountNo]
    })
  );
};

export const saveProfitLossItem = (id: number, accountNo: AccountKey, instrument: string, quantity?: number, averagePrice?: number) => {
  return async (dispatch: Dispatch<any>) => {
    const request: CmcWebApi.Account.ProfitLossItemSaveRequest1 = {
      ProfitLossItem: {
        AccountNumber: accountNo,
        AveragePrice: averagePrice,
        HoldingQuantity: quantity,
        Id: id,
        IsUserAveragePrice: averagePrice !== undefined,
        IsUserHoldingQuantity: quantity !== undefined,
        StockCode: instrument
      } as CmcWebApi.Account.ProfitLossItemSaveRequest1['ProfitLossItem']
    };
    dispatch(actions.profitLossItemSaved(request.ProfitLossItem));
    try {
      await frontendActions.webTradingApiCall(ACCOUNT_STORE_NAME, ACCOUNT_API_NAMES.ProfitLossItemSave2, request, dispatch, id, false, false, false, true);
    } catch {}
  };
};
